import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, X } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatMinutes } from '../lib/format';
import { overtimeTone, type OvertimeDay, type OvertimeDetail } from '../lib/overtime';
import { Badge, Button, Card, CardHeader, Detail, ErrorState, LoadingBlock, PageHeader, useToast } from '../components/ui';
import { overtimeStatusLabel } from './Overtime';

/**
 * Route: `/overtime/:id` — one overtime: whose, which day, how much at most and
 * why; who sent and who approved it, the employee's answer; and the day itself —
 * what it owed, what was worked, what it pays. Deciding, answering or cancelling
 * it, for whoever may. Its notifications open here, the phone's on the web.
 */
export function OvertimeDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: overtime, isLoading, error, refetch } = useQuery({
    queryKey: ['overtime', 'one', id],
    queryFn: () => api.get<OvertimeDetail>(`/overtime/${id}`),
  });

  const act = useMutation({
    mutationFn: (action: 'approve' | 'accept' | 'cancel') => api.post(`/overtime/${id}/${action}`, {}),
    onSuccess: (_result, action) => {
      toast.success(
        t(action === 'approve' ? 'overtime.approvedToast' : action === 'accept' ? 'overtime.acceptedToast' : 'overtime.cancelledToast'),
      );
      void queryClient.invalidateQueries({ queryKey: ['overtime'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !overtime) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const own = overtime.userId === user?.id;
  // Up to my overtime, to the queue it waits in, or to the team's.
  const back = own ? '/overtime?tab=mine' : overtime.can.decide ? '/overtime?tab=review' : '/overtime?tab=team';
  const { can } = overtime;
  const decidedLabel = overtime.status === 'REJECTED' ? t('overtime.rejectedBy') : t('overtime.approvedBy');

  return (
    <>
      <div className="mb-4">
        <Link to={back} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('overtime.title')}
        </Link>
      </div>

      <PageHeader
        title={t('overtime.on', { date: formatDate(overtime.workDate, locale) })}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={overtimeTone(overtime.status)}>{overtimeStatusLabel(overtime.status, own, t)}</Badge>
            {!own && <span>{localized(overtime.user)}</span>}
            <span className="tabular">{t('overtime.upToTime', { time: formatMinutes(overtime.minutes, locale) })}</span>
          </span>
        }
        action={
          (can.decide || can.answer || can.cancel) && (
            <>
              {can.decide && (
                <>
                  <Button
                    variant="primary"
                    icon={<Check className="h-4 w-4" />}
                    loading={act.isPending && act.variables === 'approve'}
                    disabled={act.isPending}
                    onClick={() => act.mutate('approve')}
                  >
                    {t('common.approve')}
                  </Button>
                  <Button variant="danger" icon={<X className="h-4 w-4" />} disabled={act.isPending} onClick={() => navigate(`/overtime/${id}/reject`)}>
                    {t('common.reject')}
                  </Button>
                </>
              )}
              {can.answer && (
                <>
                  <Button
                    variant="primary"
                    icon={<Check className="h-4 w-4" />}
                    loading={act.isPending && act.variables === 'accept'}
                    disabled={act.isPending}
                    onClick={() => act.mutate('accept')}
                  >
                    {t('overtime.accept')}
                  </Button>
                  <Button variant="danger" icon={<X className="h-4 w-4" />} disabled={act.isPending} onClick={() => navigate(`/overtime/${id}/decline`)}>
                    {t('overtime.decline')}
                  </Button>
                </>
              )}
              {can.cancel && (
                <Button loading={act.isPending && act.variables === 'cancel'} disabled={act.isPending} onClick={() => act.mutate('cancel')}>
                  {t('overtime.cancel')}
                </Button>
              )}
            </>
          )
        }
      />

      {can.answer && (
        <div className="mb-5 rounded-md border border-primary/30 bg-primary/8 px-4 py-2.5 text-body-sm text-[var(--text-primary)]">
          {t('overtime.answerNote')}
        </div>
      )}
      {overtime.monthSignedOff && (
        <div className="mb-5 rounded-md border border-warning/40 bg-warning-bg px-4 py-2.5 text-body-sm text-warning-fg dark:bg-warning/14 dark:text-warning-dark">
          {t('overtime.monthSignedOff')}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          {overtime.reviewNote && (
            <Card className={overtime.status === 'REJECTED' ? 'border-error/40' : 'border-primary/30'}>
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">
                {overtime.reviewedBy ? t('overtime.noteFrom', { name: localized(overtime.reviewedBy) }) : t('overtime.note')}
              </p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">{overtime.reviewNote}</p>
            </Card>
          )}
          {overtime.answerNote && (
            <Card className="border-error/40">
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">
                {t('overtime.noteFrom', { name: localized(overtime.user) })}
              </p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">{overtime.answerNote}</p>
            </Card>
          )}

          <Card>
            <CardHeader title={t('overtime.details')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('common.employee')}>
                <span className="inline-flex flex-col items-end">
                  <span>{localized(overtime.user)}</span>
                  {overtime.department && <span className="text-caption text-muted">{localized(overtime.department)}</span>}
                </span>
              </Detail>
              <Detail label={t('overtime.day')}>
                <span className="tabular">{formatDate(overtime.workDate, locale)}</span>
              </Detail>
              <Detail label={t('overtime.upTo')}>
                <span className="tabular">{formatMinutes(overtime.minutes, locale)}</span>
              </Detail>
              <Detail label={t('overtime.sentBy')}>
                <span className="inline-flex flex-col items-end">
                  <span>{overtime.requestedBy ? localized(overtime.requestedBy) : '—'}</span>
                  <span className="text-caption text-muted tabular">{formatDateTime(overtime.createdAt, locale)}</span>
                </span>
              </Detail>
              {overtime.reviewedBy && (
                <Detail label={decidedLabel}>
                  <span className="inline-flex flex-col items-end">
                    <span>{localized(overtime.reviewedBy)}</span>
                    {overtime.reviewedAt && (
                      <span className="text-caption text-muted tabular">{formatDateTime(overtime.reviewedAt, locale)}</span>
                    )}
                  </span>
                </Detail>
              )}
              {overtime.answeredAt && (
                <Detail label={overtime.status === 'DECLINED' ? t('overtime.declinedOn') : t('overtime.acceptedOn')}>
                  <span className="tabular">{formatDateTime(overtime.answeredAt, locale)}</span>
                </Detail>
              )}
              {overtime.cancelledAt && (
                <Detail label={t('overtime.cancelledBy')}>
                  <span className="inline-flex flex-col items-end">
                    <span>{overtime.cancelledBy ? localized(overtime.cancelledBy) : '—'}</span>
                    <span className="text-caption text-muted tabular">{formatDateTime(overtime.cancelledAt, locale)}</span>
                  </span>
                </Detail>
              )}
            </dl>
            <div className="mt-4 border-t hairline pt-4">
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('common.reason')}</p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">{overtime.reason}</p>
            </div>
          </Card>
        </div>

        <div className="min-w-0">
          <DayCard day={overtime.day} paidMinutes={overtime.status === 'ACCEPTED' ? overtime.paidMinutes : null} accepted={overtime.status === 'ACCEPTED'} />
        </div>
      </div>
    </>
  );
}

/** What the day owes, in the reader's words: "Morning — its Standard hours", "A day off" … */
export function dayKindLabel(day: OvertimeDay, t: (key: TranslationKey, vars?: Record<string, string | number>) => string, localized: (row: any) => string): string {
  if (day.kind === 'SHIFT') return t('overtime.dayKind.SHIFT', { shift: day.shift ? localized(day.shift) : '' });
  if (day.kind === 'HOLIDAY' && day.holiday) return t('overtime.dayKind.HOLIDAY_NAMED', { name: localized(day.holiday) });
  return t(`overtime.dayKind.${day.kind}` as TranslationKey);
}

/** The day itself: what it owed, what was worked and how much lay beyond — what overtime on it pays. */
export function DayCard({ day, paidMinutes, accepted }: { day: OvertimeDay; paidMinutes: number | null; accepted: boolean }) {
  const { t, locale, localized } = useI18n();
  return (
    <Card>
      <CardHeader title={t('overtime.theDay')} subtitle={t('overtime.theDayNote')} />
      <dl className="divide-y divide-[var(--line)]">
        <Detail label={t('overtime.dayIs')}>{dayKindLabel(day, t, localized)}</Detail>
        <Detail label={t('overtime.owes')}>
          <span className="tabular">{day.owedMinutes == null ? '—' : formatMinutes(day.owedMinutes, locale)}</span>
        </Detail>
        {day.judged ? (
          <>
            <Detail label={t('overtime.worked')}>
              <span className="tabular">{formatMinutes(day.workedMinutes, locale)}</span>
            </Detail>
            <Detail label={t('overtime.beyond')}>
              <span className="tabular">{formatMinutes(day.beyondMinutes, locale)}</span>
            </Detail>
            {accepted && (
              <Detail label={t('overtime.paysLabel')}>
                <span className="tabular font-medium">{formatMinutes(paidMinutes, locale)}</span>
              </Detail>
            )}
          </>
        ) : (
          <p className="pt-2 text-body-sm text-muted">{t('overtime.notJudged')}</p>
        )}
      </dl>
    </Card>
  );
}
